import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DivergenceBraid from '../components/DivergenceBraid';
import DivergenceMethodology from '../components/DivergenceMethodology';
import DivergenceReadingRoom from '../components/DivergenceReadingRoom';
import { useDivergence } from '../hooks/useDivergence';
import { useGraphIndex } from '../hooks/useGraphIndex';
import { useLang } from '../hooks/useLang';
import './Divergence.css';

export default function Divergence() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang] = useLang();
  const graph = useGraphIndex();
  const div = useDivergence();

  const chapterParam = searchParams.get('chapter');
  const verseParam = searchParams.get('verse');
  const chapterN = parseParam(chapterParam, 1, 133);
  const verseN = parseParam(verseParam, 1, 1330);
  const mode: 'overview' | 'detail' = chapterN != null ? 'detail' : 'overview';

  // Local mirror so we can update the URL without waiting for a rerender.
  const [busy] = useState(false);

  const onChapterEnter = useCallback(
    (n: number) => {
      setSearchParams((p) => {
        const q = new URLSearchParams(p);
        q.set('chapter', String(n));
        q.delete('verse');
        return q;
      });
    },
    [setSearchParams],
  );
  const onChapterBack = useCallback(() => {
    setSearchParams((p) => {
      const q = new URLSearchParams(p);
      q.delete('chapter');
      q.delete('verse');
      return q;
    });
  }, [setSearchParams]);
  const onVerseSelect = useCallback(
    (n: number) => {
      setSearchParams((p) => {
        const q = new URLSearchParams(p);
        q.set('verse', String(n));
        return q;
      });
    },
    [setSearchParams],
  );
  const onCloseVerse = useCallback(() => {
    setSearchParams((p) => {
      const q = new URLSearchParams(p);
      q.delete('verse');
      return q;
    });
  }, [setSearchParams]);

  const chapter = useMemo(() => {
    if (!div.data || chapterN == null) return null;
    return div.data.chapters.find((c) => c.n === chapterN) ?? null;
  }, [div.data, chapterN]);

  const selectedVerse = useMemo(() => {
    if (!div.data || verseN == null) return null;
    return div.data.verses[String(verseN)] ?? null;
  }, [div.data, verseN]);

  const selectedKural = useMemo(() => {
    if (!graph.data || verseN == null) return null;
    return graph.data.kurals[verseN - 1] ?? null;
  }, [graph.data, verseN]);

  // If the URL points at a verse whose chapter isn't loaded yet, derive chapter
  // from the verse's own adhigaram — keeps deep-links coherent.
  useEffect(() => {
    if (!div.data || !verseN) return;
    if (chapterN != null) return;
    const v = div.data.verses[String(verseN)];
    if (!v) return;
    setSearchParams((p) => {
      const q = new URLSearchParams(p);
      q.set('chapter', String(v.adhigaram_n));
      return q;
    });
  }, [div.data, verseN, chapterN, setSearchParams]);

  // Scroll the reading room into view on open.
  useEffect(() => {
    if (verseN == null) return;
    const el = document.getElementById('divergence-reading-room');
    if (!el) return;
    const prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: prefersReduce ? 'auto' : 'smooth', block: 'nearest' });
  }, [verseN]);

  if (graph.error || div.error) {
    return (
      <div className="focus-error" role="alert">
        <p>Sorry — we couldn't load the divergence data. Refresh to try again.</p>
      </div>
    );
  }
  if (graph.loading || div.loading || !graph.data || !div.data) {
    return (
      <div className="focus-loading" aria-live="polite">
        <span className="focus-loading__dot" aria-hidden="true" />
        <span>Loading the two threads…</span>
      </div>
    );
  }

  return (
    <div className="divergence">
      <header className="divergence__hero">
        <p className="divergence__eyebrow">Manakkudavar × Parimelazhagar · 1,330 verses judged</p>
        <h1 className="divergence__title">
          {lang === 'ta' ? (
            <>
              <span lang="ta" className="divergence__title-ta">உரையாசிரியர்கள் எங்கே பிரிகிறார்கள்</span>
              <span className="divergence__title-sub">Where the commentators part ways</span>
            </>
          ) : (
            <>
              <span className="divergence__title-en">Where the commentators part ways</span>
              <span lang="ta" className="divergence__title-sub">உரையாசிரியர்கள் எங்கே பிரிகிறார்கள்</span>
            </>
          )}
        </h1>
        <p className="divergence__lede">
          Two commentators, seven hundred years apart, on the same couplet.
          The two threads below run through all 1,330 verses. Wherever they
          separate, the commentators disagree. Click a chapter to enter it,
          then click a verse to open the reading room.
        </p>
      </header>

      <nav className="divergence__crumb" aria-label="Braid navigation">
        {mode === 'detail' ? (
          <>
            <button
              type="button"
              className="divergence__back"
              onClick={onChapterBack}
            >
              ◂ All 133 chapters
            </button>
            <span className="divergence__where">
              அதிகாரம் {chapter?.n} ·{' '}
              <span lang="ta">{chapter?.title_ta}</span> · {chapter?.title_en}
            </span>
          </>
        ) : (
          <span className="divergence__where">
            <span lang="ta">அறம் · பொருள் · இன்பம்</span> — 133 chapters, one braid
          </span>
        )}
        {busy && <span className="divergence__busy" aria-live="polite">…</span>}
      </nav>

      <DivergenceBraid
        index={div.data}
        mode={mode}
        chapterN={chapterN}
        onChapterEnter={onChapterEnter}
        onVerseSelect={onVerseSelect}
      />

      <div className="divergence__legend">
        <span>
          <span className="divergence__chip divergence__chip--m" aria-hidden="true" />
          <span lang="ta">மணக்குடவர்</span> · Manakkudavar
        </span>
        <span>
          <span className="divergence__chip divergence__chip--p" aria-hidden="true" />
          <span lang="ta">பரிமேலழகர்</span> · Parimelazhagar
        </span>
        <span className="divergence__legend-hint">
          {mode === 'overview'
            ? 'Hover a chapter to preview · click to enter'
            : 'Click a verse to open the reading room'}
        </span>
      </div>

      <div id="divergence-reading-room" className="divergence__room">
        {selectedVerse && selectedKural && chapter && (
          <DivergenceReadingRoom
            kural={selectedKural}
            divergence={selectedVerse}
            chapter={chapter}
            onClose={onCloseVerse}
          />
        )}
      </div>

      <DivergenceMethodology />
    </div>
  );
}

function parseParam(v: string | null, min: number, max: number): number | null {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isInteger(n) || n < min || n > max) return null;
  return n;
}
